<template>
    <fieldset :disabled="!isEditing.value" class="d-flex gap-3 flex-column">
        <div v-for="(action, index) in playbook.actions" :key="index" class="action-item ">
            <div class="d-flex align-items-center gap-3">
                <a data-bs-toggle="collapse" :href="`#collapse-${index}`" role="button" aria-expanded="false"
                    aria-controls="collapseExample">
                    <Icon class="icon text-danger" icon="mdi:chevron-down" />
                </a>
                <div class="d-flex gap w-100 justify-content-between" style="background: lightgray">
                    <div>
                        <button class="btn btn-sm btn-secondary ms-2" @click="moveActionUp(index)"
                            :disabled="index === 0">
                            ↑
                        </button>
                        <button class="btn btn-sm btn-secondary ms-1" @click="moveActionDown(index)"
                            :disabled="index === playbook.actions.length - 1">
                            ↓
                        </button>
                    </div>

                    <div class="d-flex justify-content-center align-items-center color">
                        {{ action.functionName ? getFunctionLabel(action.functionName) : '' }}
                    </div>

                    <button type="button" class="btn btn-sm" @click="removeAction(index)">
                        <Icon class="icon text-danger" icon="mdi:trash" />
                    </button>
                </div>
            </div>
            <div class="collapse" :id="`collapse-${index}`">
                <ActionEdit @select-function="updateVariables" :functionsList="functionsList" :key="index"
                    :ref="`childComponent${index}`" v-model="playbook.actions[index]" :companyId="companyId"
                    :sectionId="sectionId" />
            </div>
        </div>

        <div class="d-flex gap-2">
            <button class="btn btn-primary" @click="addAction">Nova Ação</button>

            <button v-if="playbook?.actions?.length > 0" class="btn btn-primary"
                @click="executePlaybook()">Executar</button>
        </div>

        <div v-if="response" :class="{
            'alert': true,
            'alert-success': response?.success,
            'alert-danger': response?.success === false
        }" style="margin-top: 20px">
            {{ response }}
        </div>

        <div v-if="this.activeJump">
            <label>
                Jump Playbook
            </label>
            <SearchableSelect :items="sharedPlaybooksOptions" :initial-selected-item="playbook.jumpPlaybook?.id"
                placeholder="Selecione um playbook" @item-selected="(value) => updateJumpPlaybook(value)" />
        </div>
    </fieldset>

    <button class="btn btn-success" @click="saveActions()">Salvar</button>
</template>

<script>
import useEditMode from "@/services/EditingService";
import ActionEdit from "./ActionEdit.vue";
import { executeFunctionSequence } from "../../services/backend/functionService";
import { getFunctionList } from '@/services/backend/functionService';
import { Icon } from "@iconify/vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import { actionService, playbookService } from "@/services/backend/ApiService";

export default {
    components: {
        ActionEdit,
        Icon,
        SearchableSelect
    },
    props: {
        modelValue: {
            type: Object,
            required: true
        },
        activeJump: {
            type: Boolean,
            required: true
        },
        companyId: {
            type: Boolean,
            required: false
        },
        sectionId: {
            type: Number,
            required: false
        }
    },
    computed: {
        playbook: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            }
        }
    },
    data() {
        return {
            response: null,
            functionsList: [],
            sharedPlaybooksOptions: []
        };
    },
    async mounted() {
        this.functionsList = await getFunctionList();
        this.updateVariables();
        const sharedPlaybooks = await playbookService.findAll({
            shared: true
        });

        this.sharedPlaybooksOptions = sharedPlaybooks.map((value) => {
            return {
                option: `ID: ${value.id} - ${value.name}`,
                value: value.id
            }
        });
    },
    methods: {
        async addAction() {
            const newAction = await actionService.create({
                executionOrder: this.playbook.actions?.length ?? 0,
                playbook: this.playbook,
                company: {
                    id: this.companyId
                }
            });

            this.playbook = {
                ...this.playbook,
                actions: [...this.playbook.actions ?? [], newAction]
            };

            this.updateVariables()
        },
        async removeAction(index) {
            await actionService.delete(this.playbook.actions[index].id)
            this.playbook.actions.splice(index, 1);
            this.updateVariables()
        },
        moveActionUp(index) {
            if (index > 0) {
                this.playbook.actions[index - 1].executionOrder = index;
                this.playbook.actions[index].executionOrder = index - 1;

                this.playbook.actions.sort((a, b) => a.executionOrder - b.executionOrder);
                this.updateVariables()
            }
        },
        moveActionDown(index) {
            if (index < this.playbook.actions.length - 1) {
                this.playbook.actions[index + 1].executionOrder = index;
                this.playbook.actions[index].executionOrder = index + 1;
                this.playbook.actions.sort((a, b) => a.executionOrder - b.executionOrder);
                this.updateVariables()
            }
        },
        selectFunction(action, selectedFunction) {
            this.updateVariables()
            action.function = selectedFunction;
            action.parameters = {};
            selectedFunction.arguments.forEach((arg) => {
                action.parameters[arg.name] = "";
            });

            this.updateVariables()
        },
        async saveActions() {
            this.$nextTick(() => {
                this.playbook.actions.forEach((_, index) => {
                    const ref = this.$refs[`childComponent${index}`];
                    if (ref && ref[0]) {
                        ref[0].saveChanges();
                    }
                });
            });

            await playbookService.update(this.playbook.id, this.playbook)
        },
        async executePlaybook() {
            let actions = [];

            for (let action of this.playbook.actions) {
                actions.push(
                    {
                        name: action.function.name,
                        args: action.parameters
                    }
                )
            }

            try {
                this.response = await executeFunctionSequence({
                    actions: actions
                })
            } catch (error) {
                this.response = error.response.data;
            }
        },
        getFunctionLabel(name) {
            const functionValue = this.functionsList.find(value => value.name == name);

            return functionValue?.label;
        },
        updateJumpPlaybook(value) {
            this.playbook.jumpPlaybook = {
                id: value
            };
        },
        updateVariables() {
            for (let [index, action] of this.playbook.actions.entries()) {
                action.variables = ['{{TICKET_ID}}', '{{PROBLEMA}}'];

                if (index !== 0) {
                    const lastAction = this.playbook.actions[index - 1];
                    const lastFunction = this.functionsList.find(f => f.name === lastAction.functionName);

                    action.variables = [...lastAction.variables];

                    if (lastFunction?.generatedVariables) {
                        const lastGenerated = Object.values(lastFunction.generatedVariables);
                        action.variables.push(...lastGenerated);
                    }
                }

                for (let i = 0; i < index; i++) {
                    const prevAction = this.playbook.actions[i];
                    const funcName = prevAction.functionName;

                    const functionCount = this.playbook.actions
                        .slice(0, index)
                        .filter(a => a.functionName === funcName).length;

                    const funcDetails = this.functionsList.find(f => f.name === funcName);

                    if (funcDetails?.generatedVariables) {
                        const generatedVars = Object.values(funcDetails.generatedVariables);

                        for (let rawVar of generatedVars) {
                            const varNameToReplace = rawVar.replace(/&(?=}})/g, functionCount.toString());

                            action.variables = action.variables.map(v =>
                                v === rawVar ? varNameToReplace : v
                            );
                        }
                    }
                }
            }
        }
    },
    setup() {
        const isEditing = useEditMode();
        return { isEditing };
    },
}
</script>

<style scoped>
.action-item {
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background-color: #f9f9f9;
}
</style>
